// src/components/Themes.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingSpinner, ErrorMessage, EmptyState } from './CommonUI';
import SetCard from './SetCard';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { themeImageUrl } from '../utils/storageUrls';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const Themes = () => {
    const [parentThemes,      setParentThemes]      = useState([]);
    const [popularThemeIds,   setPopularThemeIds]   = useState([]);
    const [searchQuery,       setSearchQuery]       = useState('');
    const [searchResults,     setSearchResults]     = useState([]);
    const [selectedSets,      setSelectedSets]      = useState({});
    // Persistent ownership: { [set_num]: { inCollection: number, inWishlist: boolean } }
    const [collectionStatus,  setCollectionStatus]  = useState({});
    const [isLoading,         setIsLoading]         = useState(true);
    const [isSearching,       setIsSearching]       = useState(false);
    const [loadedThemeImages, setLoadedThemeImages] = useState({});
    const [error,             setError]             = useState(null);
    const navigate = useNavigate();
    const { user } = useAuth();

    useDocumentTitle('Add sets');

    useEffect(() => {
        axios.defaults.withCredentials = true;
        Promise.all([
            axios.get(`${process.env.REACT_APP_API_URL}/get_parent_themes.php`),
            axios.get(`${process.env.REACT_APP_API_URL}/get_popular_theme_ids.php`)
        ]).then(([themesRes, popularRes]) => {
            setParentThemes(themesRes.data);
            setPopularThemeIds(popularRes.data);
            setIsLoading(false);
        }).catch(() => {
            setError('Unable to load themes. Please try again later.');
            setIsLoading(false);
        });
    }, []);

    // Sort themes: popular first (by popularThemeIds order), then alphabetical
    const getSortedThemes = () => {
        return [...parentThemes].sort((a, b) => {
            const aPop = popularThemeIds.includes(Number(a.id));
            const bPop = popularThemeIds.includes(Number(b.id));
            if (aPop && !bPop) return -1;
            if (!aPop && bPop) return 1;
            if (aPop && bPop) return popularThemeIds.indexOf(Number(a.id)) - popularThemeIds.indexOf(Number(b.id));
            return a.name.localeCompare(b.name);
        });
    };

    const handleThemeClick   = (id)  => navigate(`/themes/${id}`);
    const handleSearchChange = (e)   => setSearchQuery(e.target.value);

    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;
        setIsSearching(true);
        setSelectedSets({});
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/search_sets.php?query=${encodeURIComponent(searchQuery)}`
            );
            const sets = res.data;
            setSearchResults(sets);

            // Seed collectionStatus from API response
            const status = {};
            sets.forEach(set => {
                const inCollection = Number(set.in_collection || 0);
                const inWishlist   = Boolean(Number(set.in_wishlist || 0));
                if (inCollection > 0 || inWishlist) {
                    status[set.set_num] = { inCollection, inWishlist };
                }
            });
            setCollectionStatus(status);
        } catch {
            setError('An error occurred while searching. Please try again.');
        }
        setIsSearching(false);
    };

    const clearSearchResults = () => {
        setSearchResults([]);
        setSearchQuery('');
        setCollectionStatus({});
    };

    // -------------------------------------------------------------------------
    // Selection helpers
    // -------------------------------------------------------------------------

    const handleQuantityChange = useCallback((setNum, quantity, e) => {
        if (e) e.stopPropagation();
        setSelectedSets(prev => ({ ...prev, [setNum]: quantity }));
    }, []);

    const toggleSelectSet = useCallback((setNum) => {
        setSelectedSets(prev =>
            prev[setNum] !== undefined
                ? (() => { const n = { ...prev }; delete n[setNum]; return n; })()
                : { ...prev, [setNum]: 1 }
        );
    }, []);

    // -------------------------------------------------------------------------
    // Add handlers
    // -------------------------------------------------------------------------

    const updateStatus = useCallback((setNum, fields) => {
        setCollectionStatus(prev => {
            const cur = prev[setNum] ?? { inCollection: 0, inWishlist: false };
            return { ...prev, [setNum]: { ...cur, ...fields } };
        });
    }, []);

    const removeFromWishlistAfterCollect = useCallback((setNum) => {
        if (!user?.user_id) return;
        axios.post(`${process.env.REACT_APP_API_URL}/remove_set_from_wishlist.php`, {
            user_id: user.user_id,
            set_num: setNum,
        }).then(res => {
            if (res.data.success) updateStatus(setNum, { inWishlist: false });
        }).catch(() => {});
    }, [user, updateStatus]);

    const addToCollection = useCallback((setNum, quantity, e) => {
        if (e) e.stopPropagation();
        const wasInWishlist = collectionStatus[setNum]?.inWishlist ?? false;

        axios.post(`${process.env.REACT_APP_API_URL}/add_to_collection.php`, {
            sets: [{ setNum, quantity }]
        }).then(res => {
            if (res.data.success) {
                updateStatus(setNum, {
                    inCollection: (collectionStatus[setNum]?.inCollection ?? 0) + quantity,
                    ...(wasInWishlist ? { inWishlist: false } : {}),
                });
                setSelectedSets(prev => { const n = { ...prev }; delete n[setNum]; return n; });
                if (wasInWishlist) removeFromWishlistAfterCollect(setNum);
            } else {
                setError('Failed to add set to collection.');
            }
        }).catch(() => setError('An error occurred while adding to collection.'));
    }, [collectionStatus, updateStatus, removeFromWishlistAfterCollect]);

    const addToWishlist = useCallback((setNum, quantity, e) => {
        if (e) e.stopPropagation();
        axios.post(`${process.env.REACT_APP_API_URL}/add_to_wishlist.php`, {
            sets: [{ setNum, quantity }]
        }).then(res => {
            if (res.data.success) {
                updateStatus(setNum, { inWishlist: true });
                setSelectedSets(prev => { const n = { ...prev }; delete n[setNum]; return n; });
            } else {
                setError('Failed to add set to wishlist.');
            }
        }).catch(() => setError('An error occurred while adding to wishlist.'));
    }, [updateStatus]);

    // -------------------------------------------------------------------------
    // Theme image helpers
    // -------------------------------------------------------------------------

    const handleThemeImageLoad  = (id) => setLoadedThemeImages(prev => ({ ...prev, [id]: true }));
    const handleThemeImageError = (e)  => { e.target.src = '/images/lego_piece_questionmark.png'; };

    // -------------------------------------------------------------------------
    // Theme list rendering
    // -------------------------------------------------------------------------

    const getThemesByLetter = () => {
        const themes       = getSortedThemes();
        const popularThemes = themes.filter(t => popularThemeIds.includes(Number(t.id)));
        const regularThemes = themes.filter(t => !popularThemeIds.includes(Number(t.id)));

        const groupedThemes = {};
        regularThemes.forEach(t => {
            const letter = t.name.charAt(0).toUpperCase();
            if (!groupedThemes[letter]) groupedThemes[letter] = [];
            groupedThemes[letter].push(t);
        });

        return { popularThemes, groupedThemes, sortedGroups: Object.keys(groupedThemes).sort() };
    };

    const renderThemesList = () => {
        const { popularThemes, groupedThemes, sortedGroups } = getThemesByLetter();
        return (
            <div className="space-y-10">
                {/* Popular */}
                {popularThemes.length > 0 && (
                    <div>
                        <h3 className="text-xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                            <FontAwesomeIcon icon="fire" className="text-red-600 mr-2" />
                            Popular Themes
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {popularThemes.map(theme => (
                                <button
                                    key={theme.id}
                                    className="flex flex-col items-center p-4 bg-white rounded-xl shadow-sm border border-red-200 transition-all duration-200 hover:shadow-md hover:scale-105 hover:border-red-400 group"
                                    onClick={() => handleThemeClick(theme.id)}
                                >
                                    <div className="h-24 w-full flex items-center justify-center mb-3 overflow-hidden">
                                        {!loadedThemeImages[theme.id] && (
                                            <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center group-hover:bg-red-200 transition-colors">
                                                <FontAwesomeIcon icon="cube" className="text-red-600 text-lg" />
                                            </div>
                                        )}
                                        <img
                                            src={themeImageUrl(theme.id)}
                                            alt={`${theme.name} theme`}
                                            className={`object-contain max-h-24 max-w-full ${loadedThemeImages[theme.id] ? 'opacity-100' : 'opacity-0'}`}
                                            onError={handleThemeImageError}
                                            onLoad={() => handleThemeImageLoad(theme.id)}
                                            style={{ transition: 'opacity 0.3s' }}
                                        />
                                    </div>
                                    <span className="text-center font-medium text-gray-800 group-hover:text-red-600 transition-colors">
                                        {theme.name}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* All themes by letter */}
                <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                        <FontAwesomeIcon icon="cubes" className="text-gray-600 mr-2" />
                        All Themes
                    </h3>

                    {/* Alphabet jump links */}
                    <div className="mb-6 border-b border-gray-200 pb-4">
                        <div className="flex flex-wrap justify-center gap-2">
                            {sortedGroups.map(letter => (
                                <a
                                    key={letter}
                                    href={`#section-${letter}`}
                                    className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-100 hover:text-red-600 font-medium transition-colors"
                                >
                                    {letter}
                                </a>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-8">
                        {sortedGroups.map(letter => (
                            <div key={letter} id={`section-${letter}`}>
                                <h4 className="text-lg font-bold text-gray-700 mb-3 border-b border-gray-100 pb-2 pl-2 bg-gray-50 rounded-lg">
                                    {letter}
                                </h4>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                    {groupedThemes[letter].map(theme => (
                                        <button
                                            key={theme.id}
                                            className="text-left py-2 px-4 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all"
                                            onClick={() => handleThemeClick(theme.id)}
                                        >
                                            <span className="block truncate">{theme.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderSearchResults = () => (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-800">
                    <FontAwesomeIcon icon="search" className="text-gray-600 mr-2" />
                    Search Results
                    {searchResults.length > 0 && (
                        <span className="ml-2 text-base font-normal text-gray-500">
                            ({searchResults.length} {searchResults.length === 1 ? 'set' : 'sets'})
                        </span>
                    )}
                </h3>
                <button
                    onClick={clearSearchResults}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                    <FontAwesomeIcon icon="arrow-left" />
                    <span>Back to Themes</span>
                </button>
            </div>

            {searchResults.length === 0 ? (
                <EmptyState
                    message="No sets found matching your search."
                    actionText="Try Another Search"
                    onAction={() => setSearchQuery('')}
                    icon="search"
                    className="py-12"
                />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {searchResults.map(set => (
                        <SetCard
                            key={set.set_num}
                            set={set}
                            status={collectionStatus[set.set_num] ?? null}
                            viewMode="grid"
                            showDetails={false}
                            selectedQuantity={selectedSets[set.set_num]}
                            onToggleSelect={toggleSelectSet}
                            onQuantityChange={handleQuantityChange}
                            onAddToCollection={addToCollection}
                            onAddToWishlist={addToWishlist}
                        />
                    ))}
                </div>
            )}
        </div>
    );

    // -------------------------------------------------------------------------
    // Root render
    // -------------------------------------------------------------------------

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            {/* Hero Banner */}
            <div className="mb-8 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl shadow-lg overflow-hidden">
                <div className="py-10 px-6 md:px-10">
                    <div className="max-w-3xl mx-auto text-center">
                        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Explore LEGO® Themes</h1>
                        <p className="text-red-100 text-lg mb-8 max-w-2xl mx-auto">
                            Browse by theme to find your favorite sets or search for specific models to add to your collection.
                        </p>

                        <form onSubmit={handleSearchSubmit} className="max-w-2xl mx-auto">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    placeholder="Search by set name or number..."
                                    className="w-full py-3 px-5 pr-12 rounded-full border-none shadow-md focus:outline-none focus:ring-2 focus:ring-yellow-400 text-gray-800"
                                />
                                <button
                                    type="submit"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center transition-colors shadow-sm"
                                >
                                    <FontAwesomeIcon icon="magnifying-glass" />
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="mb-6">
                    <ErrorMessage message={error} onRetry={() => setError(null)} />
                </div>
            )}

            {/* Loading */}
            {(isLoading || isSearching) ? (
                <div className="py-12">
                    <LoadingSpinner
                        text={isSearching ? 'Searching for sets...' : 'Loading themes...'}
                        size="lg"
                    />
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 md:p-8">
                    {searchResults.length > 0 ? renderSearchResults() : renderThemesList()}
                </div>
            )}
        </div>
    );
};

export default Themes;
